import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  FileText,
  Heart,
  Microscope,
  PillBottle,
  Trash2,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useUploadProductDocument } from '@/features/product/hooks';
import { useMe } from '@/features/auth/hooks';
import { PageHero } from '@/components/layout/PageHero';
import { CountryPathwayStep } from '@/features/product/components/CountryPathwayStep';
import { useProductTypeEnablement } from '@/features/country/hooks';
import { isPathwaySelectionComplete } from '@/features/product/submission-pathways';
import { setEuIdentifiers, setSubmissionPathways, uploadSupportingDocumentsDirect } from '@/features/product/api';
import type { EuIdentifiers } from '@/features/product/api';
import { EuIdentifiersFields } from '@/features/product/components/EuIdentifiersFields';
import {
  EMPTY_EU_IDENTIFIERS,
  hasEuIdentifiers,
  keptEuIdentifiers,
  showsEuIdentifiers,
} from '@/features/product/eu-identifiers';
import {
  describeBatchFailures,
  type UploadProgress,
} from '@/features/product/upload-batches';
import {
  isAllowedSupportingFile,
  SUPPORTING_DOC_MAX_BYTES,
} from '@/features/product/components/SupportingDocumentsUploader';
import {
  SupportingDocsQuota,
  type QuotaRejection,
} from '@/features/product/components/SupportingDocsQuota';

/**
 * Narrower than ``ProductType`` (which includes ``'combination'``). The
 * type-picker UI only offers the three wizard-supported categories;
 * combination products go through the upload (PDF/DOCX) path because the
 * wizard's section schema doesn't model combo-product fields. Matches
 * the wizard route's ``searchSchema.type`` enum exactly.
 */
type WizardCategory = 'device' | 'drug' | 'biologic';

/**
 * Two-step product-creation entry. The user picks:
 *   1. Product category (device / drug / biologic)
 *   2. How — Build with Wizard, or Upload PDF/DOCX
 *
 * Country is intentionally NOT collected here. The technical spec is
 * country-agnostic; the same product record drives submissions in
 * multiple countries. Country is picked at agent-dispatch time
 * (predicate finder, strategy, protocol, report, dossier).
 */
export const Route = createFileRoute('/product-account_/new')({
  component: TypePickerPage,
});

interface ProductChoice {
  type: WizardCategory;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}

const CHOICES: ProductChoice[] = [
  {
    type: 'device',
    label: 'Medical Device / IVDR',
    description:
      'Instruments, implants, or software intended for medical use — including in-vitro diagnostics (IVDs).',
    icon: Heart,
  },
  {
    type: 'drug',
    label: 'Drug',
    description:
      'Chemically synthesized, small-molecule therapeutics.',
    icon: PillBottle,
  },
  {
    type: 'biologic',
    label: 'Biologic',
    description:
      'Therapeutics derived from living sources — vaccines, blood and plasma products, gene and cell therapies, and biosimilars.',
    icon: Microscope,
  },
];

export function TypePickerPage() {
  // Narrower than ProductType: the type-picker UI only offers the three
  // wizard-supported categories. Combination products go through the
  // upload-JSON path (no wizard hydration today), so they're not in this
  // picker. Matches the wizard route's ``searchSchema.type`` enum
  // (``'device' | 'drug' | 'biologic'``).
  const [category, setCategory] = useState<WizardCategory | null>(null);
  const [pathways, setPathways] = useState<Record<string, string>>({});
  // The EU device identifiers (Basic UDI-DI / UDI-DI) asked under the EU classification; saved
  // onto the product right after the upload creates it, like the classification itself.
  const [euIds, setEuIds] = useState<EuIdentifiers>(EMPTY_EU_IDENTIFIERS);
  // Step 3 (upload) unlocks only once Step 1 (category) AND Step 2 (a
  // classification for every picked country) are complete — a guided flow.
  const uploadReady = category !== null && isPathwaySelectionComplete(pathways);

  // Raised by the upload card while its submit is in flight. Step 1 locks for the
  // duration: the submit closure already captured the OLD category, so switching
  // mid-upload would clear the form and then navigate into the product the user thought
  // they had abandoned. Closing the window is simpler than unwinding it.
  const [uploading, setUploading] = useState(false);

  // Changing the category invalidates everything downstream of it. The Step-2
  // map is keyed by COUNTRY code — identical across product types — so without
  // this a device classification survives into a drug: the dropdown shows
  // nothing selected (the token isn't a drug option) while
  // isPathwaySelectionComplete still counts the non-empty value as done, and
  // submit persists it onto the new product. Re-selecting the SAME category is
  // a no-op so an accidental re-click can't discard the user's work.
  const selectCategory = (next: WizardCategory) => {
    if (next === category) return;
    setCategory(next);
    setPathways({});
    setEuIds(EMPTY_EU_IDENTIFIERS);
  };

  return (
    <>
      <PageHero
        title="Create New Product"
        subtitle={<>Pick the regulatory category, choose the countries and regulatory classifications you&rsquo;re targeting, then upload your technical specifications.</>}
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-8">
        <CategoryStep selected={category} onSelect={selectCategory} locked={uploading} />

        {/* Both keyed on the category so a change REMOUNTS them, clearing the state
            they own internally (the country picker's active tab, and the upload
            card's staged spec + supporting files). Resetting `pathways` alone would
            leave a device's staged documents attached to a drug. Keyed individually
            rather than via a shared wrapper — `main` spaces its DIRECT children
            (`space-y-8`), so an extra element would swallow the gap. */}
        <div key={`step2-${category ?? 'none'}`}>
          <StepEyebrow num={2} complete={isPathwaySelectionComplete(pathways)}>
            Countries &amp; Classification
          </StepEyebrow>
          <h2 className="text-navy text-[22px] font-bold m-0 -tracking-[0.2px] mb-4">
            How is your product classified?
          </h2>
          <CountryPathwayStep
            productType={category}
            value={pathways}
            onChange={setPathways}
            // Inside the European Union tab only (Marc 2026-10-22: it showed on the US tab too).
            renderCountryExtra={(c) =>
              c === 'EU' && showsEuIdentifiers(category, pathways) ? (
                <div className="mt-5">
                  <EuIdentifiersFields value={euIds} onChange={setEuIds} />
                </div>
              ) : null
            }
          />
        </div>

        <HowStep
          key={`step3-${category ?? 'none'}`}
          category={category}
          ready={uploadReady}
          pathways={pathways}
          euIds={showsEuIdentifiers(category, pathways) ? euIds : EMPTY_EU_IDENTIFIERS}
          onUploadingChange={setUploading}
        />
      </main>
    </>
  );
}

// ── Step 1 — Category ───────────────────────────────────────────────────

function CategoryStep({
  selected,
  onSelect,
  locked,
}: {
  selected: WizardCategory | null;
  onSelect: (t: WizardCategory) => void;
  /** True while an upload is running — changing the category would strand it. */
  locked: boolean;
}) {
  // Admin Target-markets dial: a switched-off category disappears from the
  // picker (existing products of that type stay visible and dispatchable).
  // Fail open while loading / on error — the backend rejects a disabled
  // type's upload anyway, so this is presentation, not enforcement.
  const { data: typeEnablement } = useProductTypeEnablement();
  const choices = CHOICES.filter((c) => typeEnablement?.[c.type] !== false);
  return (
    <div>
      <StepEyebrow num={1} complete={selected !== null}>
        Product Category
      </StepEyebrow>
      <h2 className="text-navy text-[22px] font-bold m-0 -tracking-[0.2px] mb-4">
        Pick the regulatory category
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {choices.map((c) => (
          <CategoryCard
            key={c.type}
            choice={c}
            active={selected === c.type}
            disabled={locked}
            onPick={() => onSelect(c.type)}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryCard({
  choice,
  active,
  disabled,
  onPick,
}: {
  choice: ProductChoice;
  active: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const Icon = choice.icon;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className={cn(
        'group relative text-left bg-white rounded-2xl p-6 transition-all',
        'flex flex-col items-start gap-4',
        disabled && 'opacity-50 pointer-events-none',
        active
          ? 'border-2 border-electric shadow-[0_6px_20px_rgba(20,40,70,0.08)]'
          : 'border border-line shadow-card hover:border-electric hover:shadow-[0_6px_20px_rgba(20,40,70,0.08)]',
      )}
    >
      <div className="size-12 rounded-xl bg-gradient-to-br from-[#1e3c72] to-[#2a5298] flex items-center justify-center text-white">
        <Icon className="size-6" aria-hidden />
      </div>
      <div>
        <div className="text-navy text-[18px] font-bold mb-1">{choice.label}</div>
        <p className="text-muted text-[13px] m-0 leading-[1.55]">
          {choice.description}
        </p>
      </div>
      {active && (
        <span className="absolute top-3.5 right-3.5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.12em] text-electric">
          <Check className="size-3.5" /> Selected
        </span>
      )}
    </button>
  );
}

// ── Step 2 — Upload ──────────────────────────────────────────────────────
// Products are created by uploading their technical specification only. The 10-section
// "Build with Wizard" form is retired (legacy wizard products were converted to sections).

function HowStep({
  category,
  ready,
  pathways,
  euIds,
  onUploadingChange,
}: {
  category: WizardCategory | null;
  ready: boolean;
  pathways: Record<string, string>;
  euIds: EuIdentifiers;
  /** Reports upload start/finish upward so Step 1 can lock while it runs. */
  onUploadingChange: (v: boolean) => void;
}) {
  return (
    <div>
      <StepEyebrow num={3}>Upload</StepEyebrow>
      <h2 className="text-navy text-[22px] font-bold m-0 -tracking-[0.2px] mb-4">
        Upload your product technical specifications
      </h2>
      {!ready && (
        <p className="text-muted text-[13px] mb-4">Pick a category above to enable the upload.</p>
      )}
      {/* grid-cols-1 (= minmax(0, 1fr)): without an explicit column the implicit track grows to
          its widest content, so a long file name widened the card past a phone/tablet screen
          and pushed the delete + Upload buttons off it. */}
      <div className="grid grid-cols-1 gap-6 max-w-2xl">
        <UploadCard ready={ready} category={category} pathways={pathways} euIds={euIds}
                    onUploadingChange={onUploadingChange} />
      </div>
    </div>
  );
}

function UploadCard({
  ready,
  category,
  pathways,
  euIds,
  onUploadingChange,
}: {
  ready: boolean;
  category: WizardCategory | null;
  pathways: Record<string, string>;
  euIds: EuIdentifiers;
  onUploadingChange: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const upload = useUploadProductDocument();
  const { data: me } = useMe();
  const extractionOn = me?.tech_spec_extraction_enabled ?? true;
  const specInputRef = useRef<HTMLInputElement>(null);
  const supportingInputRef = useRef<HTMLInputElement>(null);
  // Nothing uploads on pick: the tech spec + any supporting docs are STAGED
  // client-side and only sent when the user presses "Upload & continue" — so a
  // product isn't created (and extraction doesn't start) until they commit.
  const [specFile, setSpecFile] = useState<File | null>(null);
  const [supportingFiles, setSupportingFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [docProgress, setDocProgress] = useState<UploadProgress | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const canSubmit = ready && specFile != null && !busy;

  // Server-computed so it matches what the upload route enforces (and so local dev,
  // which has no direct-to-storage path and a much smaller cap, tells the truth).
  const specMaxBytes = me?.tech_spec_upload_max_bytes ?? null;
  const specMaxMb = specMaxBytes == null ? null : Math.floor(specMaxBytes / (1024 * 1024));
  // Supporting-document ceilings (admin dials). Defaults match the server's so the
  // screen still behaves sensibly on the first render, before /me resolves.
  // What the last selection could not take, kept on screen until the next
  // successful add — a toast alone vanishes and users missed it entirely.
  const [supportingRejection, setSupportingRejection] =
    useState<QuotaRejection | null>(null);
  const supportingMaxFiles = me?.supporting_docs_max_files ?? 30;
  const supportingMaxBytes = me?.supporting_docs_max_bytes ?? 250 * 1024 * 1024;
  const supportingMaxFileBytes =
    me?.supporting_docs_max_file_bytes ?? SUPPORTING_DOC_MAX_BYTES;

  const pickSpec = (file: File | null) => {
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      toast.error('Please pick a PDF or DOCX file.');
      return;
    }
    // Reject over-sized files HERE, not after the transfer. This upload can go
    // straight to cloud storage, so without this the user waits out a long upload
    // before the server refuses it.
    if (specMaxBytes != null && file.size > specMaxBytes) {
      const fileMb = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(
        `${file.name} is ${fileMb} MB — the limit is ${specMaxMb} MB. Please upload a smaller file.`,
      );
      return;
    }
    setSpecFile(file);
  };

  const addSupporting = (fileList: FileList | null) => {
    if (!fileList) return;
    const valid: File[] = [];
    for (const f of Array.from(fileList)) {
      if (!isAllowedSupportingFile(f)) {
        toast.error(`${f.name}: only PDF, DOCX, XLSX, CSV are allowed.`);
        continue;
      }
      if (f.size > supportingMaxFileBytes) {
        toast.error(
          `${f.name}: exceeds the ${Math.floor(supportingMaxFileBytes / (1024 * 1024))} MB per-file limit.`,
        );
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) {
      if (supportingInputRef.current) supportingInputRef.current.value = '';
      return;
    }
    // Enforce the product quota WHILE STAGING. These files are held in the browser
    // and sent in one request on submit, so without this the list grows unbounded and
    // the whole batch is refused at the end — after the user has waited. There is no
    // product yet, so the ceilings come from /me (the dials) with nothing used.
    // Partition against the CURRENT staged list, outside any state updater: updater
    // functions must be pure (React StrictMode invokes them twice), so the rejection
    // state is set here rather than as a side effect inside one.
    // Partial-add beats rejecting the whole selection — re-picking 50 files to drop
    // one is worse for the user.
    const room = Math.max(0, supportingMaxFiles - supportingFiles.length);
    let total = supportingFiles.reduce((sum, f) => sum + f.size, 0);
    const accepted: File[] = [];
    const refused: string[] = [];
    const duplicates: string[] = [];
    // Documents are keyed by FILENAME on the product, so staging the same name twice
    // would consume two slots and its bytes twice, then collapse to one document on
    // upload — the user would be charged quota for a file that never lands.
    const staged = new Set(supportingFiles.map((f) => f.name));
    let reason: QuotaRejection['reason'] = 'count';
    let stoppedAt: string | null = null;

    for (const f of valid) {
      if (staged.has(f.name)) {
        duplicates.push(f.name);
        continue;
      }
      staged.add(f.name);
      if (accepted.length >= room) {
        if (stoppedAt === null) {
          reason = 'count';
          stoppedAt = f.name;
        }
        refused.push(f.name);
        continue;
      }
      if (total + f.size > supportingMaxBytes) {
        if (stoppedAt === null) {
          reason = 'size';
          stoppedAt = f.name;
        }
        refused.push(f.name);
        continue;
      }
      total += f.size;
      accepted.push(f);
    }

    // A ceiling is the more urgent thing to report; duplicates are only noise when
    // the user is also out of room.
    setSupportingRejection(
      refused.length > 0
        ? { names: refused, reason, stoppedAt }
        : duplicates.length > 0
          ? { names: duplicates, reason: 'duplicate', stoppedAt: null }
          : null,
    );
    if (accepted.length > 0) setSupportingFiles((prev) => [...prev, ...accepted]);
    if (supportingInputRef.current) supportingInputRef.current.value = '';
  };

  const removeSupporting = (idx: number) => {
    setSupportingFiles((prev) => prev.filter((_, i) => i !== idx));
    // Removing frees room, so the "not added" notice no longer describes reality.
    setSupportingRejection(null);
  };

  const submit = async () => {
    if (!canSubmit || !specFile) return;
    setBusy(true);
    // Lock Step 1 for the duration. Only lowered on FAILURE below — a success navigates
    // away, so the category must stay locked until the page is gone.
    onUploadingChange(true);
    // Set when a supporting document is rejected; decides whether the success toast is shown
    // at all (see the single-toast note below).
    let docWarning: string | null = null;
    try {
      const res = await upload.mutateAsync({ file: specFile, productType: category! });
      // Persist the Step-2 country/pathway pick onto the just-created product.
      // Best-effort: the product exists regardless — if this fails the finder's
      // legacy grace keeps it eligible until the pathway is set from the editor.
      if (Object.keys(pathways).length > 0) {
        try {
          await setSubmissionPathways(res.product_id, pathways);
        } catch {
          toast.warning(
            'Product saved, but the country/pathway selection did not save — you can set it from the product later.',
          );
        }
      }
      // The EU device identifiers typed under the EU classification — best-effort like the
      // classification: they can be set from the product's edit page later.
      if (hasEuIdentifiers(euIds)) {
        try {
          await setEuIdentifiers(res.product_id, keptEuIdentifiers(euIds));
        } catch {
          toast.warning(
            'Product saved, but the Basic UDI-DI / UDI-DI did not save — you can set them from the product later.',
          );
        }
      }
      // Upload the staged supporting docs onto the new product. Best-effort: the
      // product + spec are fine regardless, and the user can add them again on
      // the review screen (which now has its own supporting-docs uploader).
      if (supportingFiles.length > 0) {
        // Browser→storage per file (see upload-direct.ts): the bytes never pass
        // through the app, and a failed file is reported by name while the rest
        // still go through.
        const result = await uploadSupportingDocumentsDirect(
          res.product_id,
          supportingFiles,
          setDocProgress,
        );
        setDocProgress(null);
        docWarning = describeBatchFailures(result.failed);
      }
      // ONE toast, not two. The supporting-doc warning used to fire here and the success
      // toast immediately after it, so the success stacked on top and hid the only message
      // naming what failed — the user never learned a document had not been accepted.
      // Emitting the warning last would still leave two toasts competing, and the warning
      // already opens with "Your tech spec uploaded", so it carries the success itself.
      // Longer duration because this fires as the page navigates away.
      if (docWarning) {
        toast.warning(`Your tech spec uploaded. ${docWarning} You can add them on the next screen.`, {
          duration: 12_000,
        });
      }
      if (res.review) {
        if (!docWarning) toast.success('Uploaded — reading your tech spec now.');
        void navigate({
          to: '/product-account/$productId/review',
          params: { productId: res.product_id },
        });
      } else {
        if (!docWarning) toast.success('Uploaded and saved.');
        void navigate({
          to: '/product-account/$productId/edit',
          params: { productId: res.product_id },
        });
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Upload failed — check the file and try again.',
      );
      setBusy(false);
      onUploadingChange(false);
    }
  };

  return (
    <div
      className={cn(
        'bg-white border border-line rounded-2xl p-6 shadow-card flex flex-col items-start gap-4',
        !ready && 'opacity-60 cursor-not-allowed',
      )}
      aria-disabled={!ready}
    >
      {/* No icon tile here: step 1's category cards have one and are clickable as a whole, so
          users clicked this one expecting a file picker — the button below is the control. */}
      <div>
        <div className="text-navy text-[18px] font-bold mb-1">Upload PDF / DOCX</div>
        <p className="text-muted text-[13px] m-0 leading-[1.55]">
          {extractionOn ? (
            <>
              Upload your product&rsquo;s technical specification as a PDF or Word
              file. We&rsquo;ll read the details into your product record for you to
              review and confirm on the next screen.
            </>
          ) : (
            <>
              Upload your product&rsquo;s technical specification as a PDF or Word
              file. It&rsquo;s stored on your product record — you&rsquo;ll add the
              details on the next screen, and can download the original document any
              time.
            </>
          )}
        </p>
        {specMaxMb != null && (
          <p className="text-muted text-[12px] m-0 mt-1">
            PDF or DOCX · up to {specMaxMb} MB
          </p>
        )}
      </div>

      {/* Staged tech spec — picking only selects it; nothing uploads yet. */}
      {specFile ? (
        <div className="w-full flex items-center justify-between gap-2 rounded-lg border border-line bg-[#fafbfd] px-3 py-2">
          <span className="flex items-center gap-2 min-w-0 text-[13px] text-ink">
            <FileText className="size-4 shrink-0 text-muted" aria-hidden />
            <span className="truncate">{specFile.name}</span>
          </span>
          <button
            type="button"
            onClick={() => setSpecFile(null)}
            disabled={busy}
            className="shrink-0 text-muted hover:text-error"
            aria-label={`Remove ${specFile.name}`}
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        </div>
      ) : (
        <Button type="button" onClick={() => specInputRef.current?.click()} disabled={!ready}>
          Pick tech spec <ArrowRight className="size-3.5" aria-hidden />
        </Button>
      )}

      {/* Supporting docs — optional, staged too. Only offered once a spec is picked. */}
      {specFile && (
        <div className="w-full">
          <div className="text-navy text-[13px] font-bold mb-1">
            Supporting documents for this product{' '}
            <span className="text-muted font-medium text-[12px]">&middot; optional, no review</span>
          </div>
          <p className="text-muted text-[12px] m-0 mb-2">
            Documents and data files that support this product&rsquo;s tech spec (PDF, DOCX,
            XLSX, CSV) &mdash; uploaded together, not reviewed.
          </p>
          <SupportingDocsQuota
            usedFiles={supportingFiles.length}
            maxFiles={supportingMaxFiles}
            usedBytes={supportingFiles.reduce((sum, f) => sum + f.size, 0)}
            maxBytes={supportingMaxBytes}
            rejection={supportingRejection}
            onDismissRejection={() => setSupportingRejection(null)}
          />
          {supportingFiles.length > 0 && (
            // Scrolls past a handful of rows: at the 30-document ceiling an unbounded
            // list pushes the drop zone and the Upload button off-screen, so the user
            // cannot reach the controls for the files they just added.
            <ul
              className={cn(
                'flex flex-col gap-1 mb-2',
                supportingFiles.length > 8 &&
                  'max-h-[20rem] overflow-y-auto rounded-lg border border-line p-1.5',
              )}
            >
              {supportingFiles.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-line bg-white px-3 py-1.5"
                >
                  <span className="flex items-center gap-2 min-w-0 text-[12.5px] text-ink">
                    <FileText className="size-3.5 shrink-0 text-muted" aria-hidden />
                    <span className="truncate">{f.name}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeSupporting(i)}
                    disabled={busy}
                    className="shrink-0 text-muted hover:text-error"
                    aria-label={`Remove ${f.name}`}
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div
            onClick={() => !busy && supportingInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!busy) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (!busy) addSupporting(e.dataTransfer.files);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!busy) supportingInputRef.current?.click();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Add supporting documents"
            className={cn(
              'w-full flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed py-6 px-4 cursor-pointer transition-colors',
              dragOver ? 'border-electric bg-state-info-bg' : 'border-line hover:border-electric',
              busy && 'opacity-60 cursor-not-allowed',
            )}
          >
            <Upload className="size-5 text-muted" aria-hidden />
            <span className="text-[13px] font-semibold text-navy">Add supporting documents</span>
            <span className="text-[12px] text-muted">
              Drag &amp; drop or click &mdash; add several at once (PDF, DOCX, XLSX, CSV)
            </span>
            {/* The per-file ceiling belongs here too: the quota block above covers the
                product totals, but a single over-sized file is refused for a different
                reason and the user should know the number before picking one. */}
            <span className="text-[12px] text-muted">
              Up to {Math.floor(supportingMaxFileBytes / (1024 * 1024))} MB per file
            </span>
          </div>
        </div>
      )}

      <p className="text-muted text-[12px] leading-[1.5] m-0">
        {extractionOn ? (
          <>
            Nothing uploads until you press Upload. You&rsquo;ll see everything we found and
            can fix anything before it&rsquo;s saved.
          </>
        ) : (
          <>
            Agent results are drafts built from the document you upload &mdash; their
            quality depends on how complete and current your technical specification
            is. Always review output before using it in a regulatory submission.
          </>
        )}
      </p>

      <div className="w-full flex justify-end pt-3 border-t border-line">
        <Button type="button" onClick={() => void submit()} disabled={!canSubmit}>
          {busy
            ? docProgress != null
              ? `Uploading documents… ${docProgress.done} of ${docProgress.total}`
              : upload.progress != null
                ? `Uploading… ${Math.round(upload.progress * 100)}%`
                : 'Uploading…'
            : 'Upload & continue'}
          {!busy && <ArrowRight className="size-3.5" aria-hidden />}
        </Button>
      </div>

      <input
        ref={specInputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          pickSpec(e.target.files?.[0] ?? null);
          if (specInputRef.current) specInputRef.current.value = '';
        }}
      />
      <input
        ref={supportingInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.xlsx,.csv"
        className="hidden"
        onChange={(e) => addSupporting(e.target.files)}
      />
    </div>
  );
}

// ── Shared step eyebrow ─────────────────────────────────────────────────

function StepEyebrow({
  num,
  children,
  complete = false,
}: {
  num: number;
  children: React.ReactNode;
  complete?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
          complete ? 'bg-state-success-accent text-white' : 'bg-navy text-white',
        )}
      >
        {complete ? <Check className="size-3.5" /> : num}
      </span>
      <span className="text-[12px] tracking-[0.16em] uppercase text-muted font-bold">
        {children}
      </span>
    </div>
  );
}
